import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook para navegação oculta de administrador via múltiplos cliques (ponto cego).
 * Exige múltiplos cliques consecutivos dentro de um limite de tempo para navegar.
 * Evita que clientes entrem acidentalmente na tela de login de admin com um único toque.
 *
 * @param {string} destination - Rota de destino (padrão: '/admin/admin')
 * @param {number} requiredClicks - Quantidade de cliques necessários (padrão: 5)
 * @param {number} timeoutMs - Janela de tempo em ms para resetar o contador (padrão: 3000ms)
 * @returns {function} handleAdminClick - Handler de clique
 */
export const useAdminBackdoor = (destination = '/admin/admin', requiredClicks = 5, timeoutMs = 3000) => {
  const navigate = useNavigate();
  const clickCountRef = useRef(0);
  const timeoutRef = useRef(null);

  const handleAdminClick = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();

    clickCountRef.current += 1;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (clickCountRef.current >= requiredClicks) {
      clickCountRef.current = 0;
      navigate(destination);
    } else {
      timeoutRef.current = setTimeout(() => {
        clickCountRef.current = 0;
      }, timeoutMs);
    }
  };

  return handleAdminClick;
};

/**
 * Componente utilitário para envolver o ponto cego com 5 cliques.
 */
export const AdminBackdoor = ({
  className,
  style,
  children,
  destination = '/admin/admin',
  requiredClicks = 5,
  timeoutMs = 3000,
}) => {
  const handleAdminClick = useAdminBackdoor(destination, requiredClicks, timeoutMs);

  return (
    <div
      className={className}
      style={style}
      onClick={handleAdminClick}
      role="button"
      tabIndex={-1}
    >
      {children}
    </div>
  );
};

export default useAdminBackdoor;
